import {
  ObjectType,
  ObjectStatus,
  type DbObject,
  type DbStartAtIndex,
} from "@/db/schema"
import type { ViewItem, ViewData } from "./hooks/useViewData"
import type { ViewId } from "./viewRegistry"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key prefix for cached placeholders */
const LS_PREFIX = "totodo:ph:"

/** Max items to cache per section */
const MAX_CACHED_ITEMS = 20

/** For grouped views: max items to cache per group */
const MAX_CACHED_PER_GROUP = 4

/** Views that use grouped caching (per-group item limit instead of flat cap) */
const GROUPED_CACHE_VIEWS: Set<ViewId> = new Set(["anyday", "someday"])

/**
 * When `true`, the placeholder cache is completely bypassed —
 * nothing is read from or written to localStorage.
 * Useful for debugging layout jumps and placeholder rendering.
 *
 * Toggle at runtime: `window.__V2_DISABLE_PH_CACHE = true`
 * Or set here to `true` during development.
 */
let CACHE_DISABLED = false

// Expose a runtime toggle on window for debugging convenience
declare global {
  interface Window {
    __V2_DISABLE_PH_CACHE?: boolean
  }
}

/** Check if cache is disabled (compile-time flag or runtime window toggle) */
function isCacheDisabled(): boolean {
  return (
    CACHE_DISABLED ||
    (typeof window !== "undefined" && window.__V2_DISABLE_PH_CACHE === true)
  )
}

/**
 * Programmatically enable or disable the placeholder cache.
 * ```ts
 * import { setPlaceholderCacheEnabled } from "@/features/views/loadingPlaceholders"
 * setPlaceholderCacheEnabled(false) // disable cache for debugging
 * ```
 */
export function setPlaceholderCacheEnabled(enabled: boolean): void {
  CACHE_DISABLED = !enabled
}

/**
 * Synchronous check: is there any cached placeholder data in localStorage?
 * Used by DatabaseProvider to decide whether to skip the loading gate.
 */
export function hasAnyCachedPlaceholder(): boolean {
  if (isCacheDisabled()) return false
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(LS_PREFIX)) return true
    }
  } catch {
    // localStorage unavailable
  }
  return false
}

/**
 * Collect all cached pika icon strings from placeholder caches.
 * Called at app startup to preload icons before React renders.
 */
export function getCachedIconStrings(): string[] {
  if (isCacheDisabled()) return []
  const icons: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith(LS_PREFIX)) continue
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as CachedPlaceholder
      if (parsed.containerIcon) icons.push(parsed.containerIcon)
      // Also collect icons from cached task items (container items in lists)
      for (const item of parsed.items) {
        if (item.task.icon) icons.push(item.task.icon)
      }
      if (parsed.laterItems) {
        for (const item of parsed.laterItems) {
          if (item.task.icon) icons.push(item.task.icon)
        }
      }
    }
  } catch {
    // Ignore parse errors
  }
  return icons
}

/**
 * DbObject fields kept in the cache — enough for TaskItem visual rendering.
 * Everything else is stripped to keep localStorage compact.
 */
const CACHED_FIELDS: (keyof DbObject)[] = [
  "id",
  "type",
  "title",
  "status",
  "tags",
  "parentPath",
  "dueAt",
  "note",
  "docHtml",
  "icon",
  "realmId",
  "owner",
  "index",
  "statusAt",
  "deletedAt",
  "userStatus",
]

// ---------------------------------------------------------------------------
// Helper — create a minimal placeholder DbObject
// ---------------------------------------------------------------------------

let phCounter = 0

/**
 * Create a placeholder task with all required DbObject fields.
 * Override any field via `overrides`.
 *
 * IDs use `$ph-{n}` prefix — never collides with real Dexie IDs and
 * makes placeholder items easy to spot in React DevTools / console.
 */
export function createPlaceholderTask(overrides?: Partial<DbObject>): DbObject {
  phCounter += 1
  return {
    id: `$ph-${phCounter}`,
    type: ObjectType.Task,
    title: "",
    parentPath: "",
    index: 0,
    status: ObjectStatus.Unfinished,
    tags: [],
    isDeleted: 0,
    createdAt: "",
    createdBy: "",
    fullTextIndex: [],
    ...overrides,
  }
}

// ---------------------------------------------------------------------------
// Per-view static placeholder config (fallback when no cache exists)
// ---------------------------------------------------------------------------

/**
 * Configuration for a single view's loading placeholder.
 *
 * - `items` → main task list (rendered by ViewShell as real TaskItem rows)
 * - `laterItems` → secondary "Someday" section (project/list views only)
 *
 * Each entry is a `Partial<DbObject>` — `createPlaceholderTask` fills the rest.
 * Tweak titles, status, taskType etc. per view to get the visual you want.
 */
export interface PlaceholderConfig {
  items: Partial<DbObject>[]
  laterItems?: Partial<DbObject>[]
}

/**
 * Static placeholder definitions — used as fallback when there is no
 * cached snapshot in localStorage (i.e. first-ever visit).
 */
export const LOADING_PLACEHOLDERS: Record<ViewId, PlaceholderConfig> = {
  // ── Static views ────────────────────────────────────────────────────────
  today: {
    items: [{}, {}, {}, {}, {}, {}, {}],
  },
  evening: {
    items: [{}, {}, {}],
  },
  inbox: {
    items: [{}, {}, {}],
  },
  upcoming: {
    items: [{}, {}, {}, {}],
  },
  anyday: {
    items: [{}, {}, {}, {}, {}],
  },
  someday: {
    items: [{}, {}, {}],
  },
  logbook: {
    items: [
      { status: ObjectStatus.Completed },
      { status: ObjectStatus.Completed },
      { status: ObjectStatus.Completed },
      { status: ObjectStatus.Completed },
    ],
  },
  trash: {
    items: [{}, {}, {}],
  },
  search: {
    items: [],
  },
  recurring: {
    items: [],
  },
  routines: {
    items: [],
  },
  laterProjects: {
    items: [{}, {}],
    laterItems: [{}, {}],
  },

  // ── Dynamic views ───────────────────────────────────────────────────────
  project: {
    items: [{}, {}, {}, {}],
    laterItems: [{}, {}],
  },
  list: {
    items: [{}, {}, {}, {}],
    laterItems: [{}, {}],
  },
  area: {
    items: [],
  },
  tag: {
    items: [],
  },
}

// ---------------------------------------------------------------------------
// localStorage cache — shape stored in storage
// ---------------------------------------------------------------------------

/** A single cached view item: slimmed task + optional startAtData */
interface CachedViewItem {
  task: Partial<DbObject>
  startAtData?: DbStartAtIndex
  /** Real sortIndex — preserves query sort order for grouped views */
  si?: number
}

interface CachedPlaceholder {
  /** Container title for dynamic views (project/list/area name) */
  containerTitle?: string
  /** Container tags for dynamic views (area/project tags) */
  containerTags?: string[]
  /** Container icon string (e.g. "pika:rocket" or "emoji:🚀") */
  containerIcon?: string
  /** Container ObjectType (Project=2, List=4, Area=3) */
  containerType?: number
  /** Container ObjectStatus (Unfinished=0, Completed=1, etc.) */
  containerStatus?: number
  /** Cached editor HTML for container notes */
  containerDocHtml?: string
  /** Cached startAt timestamp (from startAtIndex table) */
  containerStartAt?: number
  /** Cached startAt type (StartAtType enum) */
  containerStartAtType?: number
  /** Cached startAt subType (StartAtSubType enum) */
  containerStartAtSubType?: number
  /** Cached dueAt timestamp */
  containerDueAt?: string
  /** Available tags for the ChoosableTags filter bar */
  viewTags?: string[]
  /** Slimmed-down items for main section */
  items: CachedViewItem[]
  /** Slimmed-down items for later/someday section */
  laterItems?: CachedViewItem[]
  /** Epoch ms when this snapshot was written */
  cachedAt: number
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Build the localStorage key for a view + optional container */
function cacheKey(viewId: ViewId, containerId?: string): string {
  return containerId
    ? `${LS_PREFIX}${viewId}:${containerId}`
    : `${LS_PREFIX}${viewId}`
}

/** Strip a DbObject down to only the fields needed for visual rendering */
function slimTask(task: DbObject): Partial<DbObject> {
  const slim: Record<string, unknown> = {}
  for (const key of CACHED_FIELDS) {
    const val = task[key]
    if (val !== undefined && val !== null && val !== "" && val !== 0) {
      // Store only a truthy marker for docHtml (can be very large)
      slim[key] = key === "docHtml" ? "1" : val
    }
  }
  return slim as Partial<DbObject>
}

/** Slim a ViewItem array → CachedViewItem[], capped at MAX_CACHED_ITEMS */
function slimViewItems(items: ViewItem[]): CachedViewItem[] {
  return items.slice(0, MAX_CACHED_ITEMS).map((vi) => {
    const cached: CachedViewItem = { task: slimTask(vi.task), si: vi.sortIndex }
    if (vi.startAtData) {
      cached.startAtData = vi.startAtData
    }
    return cached
  })
}

/**
 * Slim items for grouped views — keeps the first N items per group so that
 * every group is represented in the placeholder (not just the first 20 flat).
 * Groups are determined by parentPath (same logic as GroupedTaskList).
 */
function slimGroupedViewItems(items: ViewItem[]): CachedViewItem[] {
  const groupCounts = new Map<string, number>()
  const result: CachedViewItem[] = []

  for (const vi of items) {
    const segments = vi.task.parentPath.split("/")
    const groupKey =
      segments.length === 2 && !segments[1]!.startsWith("#")
        ? segments[1]!
        : "__root__"

    const count = groupCounts.get(groupKey) ?? 0
    if (count >= MAX_CACHED_PER_GROUP) continue
    groupCounts.set(groupKey, count + 1)

    const cached: CachedViewItem = { task: slimTask(vi.task), si: vi.sortIndex }
    if (vi.startAtData) {
      cached.startAtData = vi.startAtData
    }
    result.push(cached)
  }
  return result
}

/** Try to read and parse a cached placeholder from localStorage */
function readCache(key: string): CachedPlaceholder | null {
  if (isCacheDisabled()) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as CachedPlaceholder
  } catch {
    return null
  }
}

/** Convert a PlaceholderConfig (static — no startAtData) into ViewData */
function configToViewData(config: PlaceholderConfig): ViewData {
  const items: ViewItem[] = config.items.map((overrides, i) => ({
    task: createPlaceholderTask(overrides),
    sortIndex: i,
  }))

  const laterItems: ViewItem[] = (config.laterItems ?? []).map(
    (overrides, i) => ({
      task: createPlaceholderTask(overrides),
      sortIndex: i,
    }),
  )

  return { items, laterItems }
}

/** Convert cached items (with startAtData) into ViewData */
function cachedToViewData(cached: CachedPlaceholder): ViewData {
  const items: ViewItem[] = cached.items.map((ci, i) => ({
    task: createPlaceholderTask(ci.task),
    sortIndex: ci.si ?? i,
    startAtData: ci.startAtData,
  }))

  const laterItems: ViewItem[] = (cached.laterItems ?? []).map((ci, i) => ({
    task: createPlaceholderTask(ci.task),
    sortIndex: ci.si ?? i,
    startAtData: ci.startAtData,
  }))

  return { items, laterItems }
}

// ---------------------------------------------------------------------------
// Public API — read placeholder
// ---------------------------------------------------------------------------

/**
 * Get a `ViewData` placeholder for the given view.
 *
 * **Priority**:
 * 1. Cached snapshot from localStorage (previous real data)
 * 2. Static fallback from `LOADING_PLACEHOLDERS`
 *
 * @param viewId - Which view to get placeholder for
 * @param containerId - Container ID for project/list/area views
 */
export function getLoadingPlaceholder(
  viewId: ViewId,
  containerId?: string,
): ViewData {
  // 1. Try localStorage cache (includes startAtData)
  const cached = readCache(cacheKey(viewId, containerId))
  if (cached) {
    return cachedToViewData(cached)
  }

  // 2. Static fallback
  return configToViewData(LOADING_PLACEHOLDERS[viewId])
}

/**
 * Get the cached container title for a dynamic view (project/list/area).
 * Returns `undefined` if no cache exists.
 */
export function getCachedContainerTitle(
  viewId: ViewId,
  containerId?: string,
): string | undefined {
  if (!containerId) return undefined
  const cached = readCache(cacheKey(viewId, containerId))
  return cached?.containerTitle
}

/**
 * Get cached container tags for a dynamic view (project/list/area).
 * Returns `undefined` if no cache exists.
 */
export function getCachedContainerTags(
  viewId: ViewId,
  containerId?: string,
): string[] | undefined {
  if (!containerId) return undefined
  const cached = readCache(cacheKey(viewId, containerId))
  return cached?.containerTags
}

/**
 * Get cached available tags for a view's ChoosableTags filter bar.
 * Returns empty array if no cache exists.
 */
export function getCachedViewTags(
  viewId: ViewId,
  containerId?: string,
): string[] {
  const cached = readCache(cacheKey(viewId, containerId))
  return cached?.viewTags ?? []
}

/**
 * Cached container metadata returned by getCachedContainerMeta.
 */
export interface CachedContainerMeta {
  title: string
  icon?: string
  type?: number
  status?: number
  tags?: string[]
  docHtml?: string
  startAt?: number
  startAtType?: number
  startAtSubType?: number
  dueAt?: string
}

/**
 * Get all cached container metadata for a dynamic view (project/list/area).
 * Returns `undefined` if no cache exists.
 */
export function getCachedContainerMeta(
  viewId: ViewId,
  containerId?: string,
): CachedContainerMeta | undefined {
  if (!containerId) return undefined
  const cached = readCache(cacheKey(viewId, containerId))
  if (!cached?.containerTitle) return undefined
  return {
    title: cached.containerTitle,
    icon: cached.containerIcon,
    type: cached.containerType,
    status: cached.containerStatus,
    tags: cached.containerTags,
    docHtml: cached.containerDocHtml,
    startAt: cached.containerStartAt,
    startAtType: cached.containerStartAtType,
    startAtSubType: cached.containerStartAtSubType,
    dueAt: cached.containerDueAt,
  }
}

/**
 * Cache container metadata independently of view data.
 * Call this from page components when the container object loads,
 * so the next visit can render title + icon + tags from cache immediately.
 */
export function cacheContainerMeta(
  viewId: ViewId,
  containerId: string,
  title: string,
  tags?: string[],
  icon?: string,
  type?: number,
  status?: number,
  docHtml?: string,
  startAt?: number,
  startAtType?: number,
  startAtSubType?: number,
  dueAt?: string,
): void {
  if (isCacheDisabled()) return
  try {
    const key = cacheKey(viewId, containerId)
    const existing = readCache(key)
    const entry: CachedPlaceholder = existing ?? {
      items: [],
      cachedAt: Date.now(),
    }
    entry.containerTitle = title
    entry.containerTags = tags && tags.length > 0 ? tags : undefined
    entry.containerIcon = icon
    entry.containerType = type
    entry.containerStatus = status
    entry.containerDocHtml = docHtml
    entry.containerStartAt = startAt
    entry.containerStartAtType = startAtType
    entry.containerStartAtSubType = startAtSubType
    entry.containerDueAt = dueAt
    entry.cachedAt = Date.now()
    localStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// ---------------------------------------------------------------------------
// Public API — write cache
// ---------------------------------------------------------------------------

/**
 * Cache the first 20 items of a view's real data into localStorage.
 * Called automatically when `useViewDataImmediate` receives real data.
 *
 * @param viewId - Which view
 * @param data - The real ViewData from Dexie
 * @param containerId - Container ID for project/list/area views
 * @param containerTitle - Container name (e.g. project title) for dynamic views
 * @param viewTags - Available tags for the ChoosableTags filter bar
 */
export function cacheViewPlaceholder(
  viewId: ViewId,
  data: ViewData,
  containerId?: string,
  containerTitle?: string,
  viewTags?: string[],
): void {
  if (isCacheDisabled()) return
  try {
    const isGrouped = GROUPED_CACHE_VIEWS.has(viewId)
    const entry: CachedPlaceholder = {
      items: isGrouped
        ? slimGroupedViewItems(data.items)
        : slimViewItems(data.items),
      cachedAt: Date.now(),
    }
    if (containerTitle) {
      entry.containerTitle = containerTitle
    }
    // Read existing cache to preserve enriched data that may have been
    // written by cacheContainerMeta or cacheEnrichedViewTags earlier in
    // this React commit cycle (child effects fire before parent effects).
    const existing = readCache(cacheKey(viewId, containerId))

    // Preserve container metadata written by cacheContainerMeta
    if (existing?.containerIcon) entry.containerIcon = existing.containerIcon
    if (existing?.containerType != null) entry.containerType = existing.containerType
    if (existing?.containerStatus != null) entry.containerStatus = existing.containerStatus
    if (existing?.containerDocHtml) entry.containerDocHtml = existing.containerDocHtml
    if (existing?.containerStartAt != null) entry.containerStartAt = existing.containerStartAt
    if (existing?.containerStartAtType != null) entry.containerStartAtType = existing.containerStartAtType
    if (existing?.containerStartAtSubType != null) entry.containerStartAtSubType = existing.containerStartAtSubType
    if (existing?.containerDueAt) entry.containerDueAt = existing.containerDueAt
    if (existing?.containerTags) {
      entry.containerTags = existing.containerTags
    }
    // Preserve enriched viewTags (own + inherited) from cache.
    // cacheEnrichedViewTags writes the complete set; the viewTags param
    // here is own-tags-only (always a subset). Keep whichever is richer.
    if (existing?.viewTags && existing.viewTags.length > 0) {
      entry.viewTags = existing.viewTags
    } else if (viewTags && viewTags.length > 0) {
      entry.viewTags = viewTags
    }
    if (data.laterItems.length > 0) {
      entry.laterItems = slimViewItems(data.laterItems)
    }
    localStorage.setItem(cacheKey(viewId, containerId), JSON.stringify(entry))
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Cache enriched view tags (own + inherited) separately.
 * Called from ViewShell where ancestor-enriched tags are computed.
 * This ensures the tag filter bar shows all tags immediately on load.
 */
export function cacheEnrichedViewTags(
  viewId: ViewId,
  tags: string[],
  containerId?: string,
): void {
  if (isCacheDisabled()) return
  if (tags.length === 0) return
  try {
    const key = cacheKey(viewId, containerId)
    const existing = readCache(key)
    if (existing) {
      existing.viewTags = tags
      existing.cachedAt = Date.now()
      localStorage.setItem(key, JSON.stringify(existing))
    } else {
      // No cache entry yet (first visit). Create a minimal entry so
      // cacheViewPlaceholder (parent effect, fires next) can pick up
      // the enriched viewTags and preserve them.
      const entry: CachedPlaceholder = {
        items: [],
        viewTags: tags,
        cachedAt: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(entry))
    }
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// ---------------------------------------------------------------------------
// Area projects cache — separate key for project/list items under an area
// ---------------------------------------------------------------------------

/**
 * DbObject fields kept when caching area project/list items.
 * Enough for AreaProjectList rendering (title, type, tags, realmId).
 */
const CACHED_PROJECT_FIELDS: (keyof DbObject)[] = [
  "id",
  "type",
  "title",
  "tags",
  "realmId",
  "status",
  "index",
]

/** Strip a project/list DbObject to rendering-essential fields */
function slimProject(project: DbObject): Partial<DbObject> {
  const slim: Record<string, unknown> = {}
  for (const key of CACHED_PROJECT_FIELDS) {
    const val = project[key]
    if (val !== undefined && val !== null && val !== "" && val !== 0) {
      slim[key] = val
    }
  }
  return slim as Partial<DbObject>
}

/**
 * Cache area project/list items to localStorage.
 * Called from `useAreaProjectsImmediate` when real data arrives.
 */
export function cacheAreaProjects(areaId: string, projects: DbObject[]): void {
  if (isCacheDisabled()) return
  try {
    const items = projects.slice(0, MAX_CACHED_ITEMS).map(slimProject)
    localStorage.setItem(
      `${LS_PREFIX}area-projects:${areaId}`,
      JSON.stringify({ items, cachedAt: Date.now() }),
    )
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Get cached area project/list items from localStorage.
 * Returns full `DbObject[]` (placeholder-filled) for use as hook placeholder.
 */
export function getCachedAreaProjects(areaId?: string): DbObject[] {
  if (!areaId || isCacheDisabled()) return []
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}area-projects:${areaId}`)
    if (!raw) return []
    const data = JSON.parse(raw) as { items: Partial<DbObject>[] }
    return data.items.map((overrides) => createPlaceholderTask(overrides))
  } catch {
    return []
  }
}
